import { computed, Injectable, signal } from '@angular/core';

/** What the cart builder's header reports about the rep's unsaved work (US 21.13). */
export type CartSaveStatus = 'saved' | 'saving' | 'failed';

/**
 * Tracks the cart builder's automatic saves so the header can say whether the rep's latest work is
 * actually safe. {@link CartSaveStatusInterceptor} counts the requests; nothing else writes here.
 *
 * A failure **latches**: the story allows "Auto-saved" only once every current change has been
 * saved, so a failed save keeps the status off `saved` until a later one succeeds — a rep must
 * never be told their work is safe while it is sitting in a rejected request.
 */
@Injectable({ providedIn: 'root' })
export class CartSaveStatusService {
  private readonly pending = signal(0);
  private readonly failed = signal(false);

  /**
   * `saving` outranks `failed`, so a retry reads as in progress rather than as still broken.
   */
  readonly status = computed<CartSaveStatus>(() => {
    if (this.pending() > 0) return 'saving';
    return this.failed() ? 'failed' : 'saved';
  });

  begin(): void {
    this.pending.update((n) => n + 1);
  }

  /** @param ok Whether the save landed. A success is what clears an earlier failure. */
  settle(ok: boolean): void {
    this.pending.update((n) => Math.max(0, n - 1));
    this.failed.set(!ok);
  }

  /**
   * A save that ended without an outcome — the caller unsubscribed. It says nothing about whether
   * the work is safe, so the count is released and the failure flag left exactly as it was; leaving
   * the count up instead would strand the header on "Saving…".
   */
  abandon(): void {
    this.pending.update((n) => Math.max(0, n - 1));
  }

  /** Back to a clean slate — a new cart must not inherit the last one's failure. */
  reset(): void {
    this.pending.set(0);
    this.failed.set(false);
  }
}
